import { BASE_URL, pythonBaseUrl } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { v4 as uuidv4 } from "uuid";

/**
 * Service for handling upload extraction operations in parallel
 * This is specifically designed for bulk import processes
 */
class UploadExtractionService {
  constructor() {
    this.generateUUID = () => uuidv4();
  }

  /**
   * Process multiple files in parallel for bulk import
   * @param {Array} selectedFiles - Array of file objects to process
   * @param {Function} onProgressUpdate - Callback for progress updates
   * @returns {Promise<Array>} - Array of processing results
   */
  async processBulkFiles(selectedFiles, onProgressUpdate = null) {
    console.log(`🚀 Starting parallel processing of ${selectedFiles.length} files`);

    // Process all files in parallel
    const processPromises = selectedFiles.map(async (file, index) => {
      try {
        // Update progress callback if provided
        if (onProgressUpdate) {
          onProgressUpdate(file.fileImportId, "processing", `Processing ${file.fileName}`);
        }

        const result = await this.processIndividualFile(file);

        // Update progress on completion
        if (onProgressUpdate) {
          const status = result.success ? "completed" : "failed";
          const message = result.success
            ? `${file.fileName} processed successfully`
            : `${file.fileName} failed: ${result.error}`;
          onProgressUpdate(file.fileImportId, status, message);
        }

        return {
          ...result,
          fileId: file.fileImportId,
          fileName: file.fileName,
          index,
        };
      } catch (error) {
        console.error(`Error processing file ${file.fileName}:`, error);

        if (onProgressUpdate) {
          onProgressUpdate(
            file.fileImportId,
            "failed",
            `${file.fileName} failed: ${error.message}`
          );
        }

        return {
          success: false,
          error: error.message,
          fileId: file.fileImportId,
          fileName: file.fileName,
          index,
        };
      }
    });

    // Wait for all files to complete processing
    const results = await Promise.allSettled(processPromises);

    // Process results
    const processedResults = results.map((result, index) => {
      if (result.status === "fulfilled") {
        return result.value;
      } else {
        return {
          success: false,
          error: result.reason?.message || "Unknown error",
          fileId: selectedFiles[index]?.fileImportId || `file-${index}`,
          fileName: selectedFiles[index]?.fileName || `Unknown file ${index}`,
          index,
        };
      }
    });

    // Log summary
    const successful = processedResults.filter((r) => r.success);
    const failed = processedResults.filter((r) => !r.success);

    console.log(
      `📊 Bulk processing completed: ${successful.length} successful, ${failed.length} failed`
    );

    return processedResults;
  }

  /**
   * Process a single file
   * @param {Object} fileData - File data object
   * @returns {Promise<Object>} - Processing result
   */
  async processIndividualFile(fileData) {
    try {
      // Determine the processing type based on uploadType
      if (fileData.uploadType >= 2 && fileData.uploadType <= 10) {
        return await this.processSingleForm(fileData);
      } else if (fileData.uploadType === 11) {
        return await this.processPackFile(fileData);
      } else {
        return await this.processRegularFile(fileData);
      }
    } catch (error) {
      console.error(`Error in processIndividualFile for ${fileData.fileName}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Process single form files (uploadType 2-10)
   */
  async processSingleForm(fileData) {
    const docType = this.getDocumentTypeByUploadType(fileData.uploadType);

    const extractionFormData = new FormData();
    extractionFormData.append("filename", fileData.fileImportId);
    extractionFormData.append("request_id", fileData.fileImportId);
    extractionFormData.append("extension", fileData.fileName.split(".").pop());
    extractionFormData.append(
      "filepath",
      `${getUserIdFromLocalStorage().organizationId}/${getUserIdFromLocalStorage().userId}/uploadfiles/${fileData.fileImportId}.pdf`
    );
    extractionFormData.append("organization_id", getUserIdFromLocalStorage().organizationId);
    extractionFormData.append("original_filename", fileData.fileName);
    extractionFormData.append("user_id", getUserIdFromLocalStorage().userId);
    extractionFormData.append("business_unit", fileData.businessUnit || "Demo");
    extractionFormData.append("doc_type", docType);
    extractionFormData.append("is_single_form", "true");
    extractionFormData.append("upload_type", String(fileData.uploadType || 2));

    const extractionUrl = `${pythonBaseUrl}/Extract/UploadExtraction`;

    const extractionResponse = await fetch(extractionUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      body: extractionFormData,
      mode: "cors",
    });

    if (extractionResponse.ok) {
      const extractionData = await extractionResponse.json();

      if (extractionData.status === "success") {
        // Extract confidence score from response
        let confidenceLevel = null;
        if (extractionData.confidence_score !== undefined) {
          confidenceLevel =
            extractionData.confidence_score < 1
              ? Math.round(extractionData.confidence_score * 100)
              : Math.round(extractionData.confidence_score);
        } else if (extractionData.reportdata?.confidenceScore !== undefined) {
          confidenceLevel =
            extractionData.reportdata.confidenceScore < 1
              ? Math.round(extractionData.reportdata.confidenceScore * 100)
              : Math.round(extractionData.reportdata.confidenceScore);
        }

        // Only extract total pages and total forms if they don't already exist
        let totalPages = fileData.totalPages;
        let totalForms = fileData.totalForms;

        // Only update if current values are null/undefined
        if (extractionData.reportdata) {
          if (
            (totalPages === null || totalPages === undefined) &&
            extractionData.reportdata.totalPages !== undefined
          ) {
            totalPages = Number(extractionData.reportdata.totalPages);
          }
          if (
            (totalForms === null || totalForms === undefined) &&
            extractionData.reportdata.totalForms !== undefined
          ) {
            // For single forms, apply WHS logic: 0 for WHS, otherwise use response value
            totalForms = Number(extractionData.reportdata.totalForms);
          }
        } else if (extractionData.statistics) {
          // Fallback to statistics if reportdata not available (only if values are null/undefined)
          if (
            (totalPages === null || totalPages === undefined) &&
            extractionData.statistics.total_pages !== undefined
          ) {
            totalPages = Number(extractionData.statistics.total_pages);
          }
          if (
            (totalForms === null || totalForms === undefined) &&
            extractionData.statistics.total_forms !== undefined
          ) {
            totalForms = Number(extractionData.statistics.total_forms);
          }
        }

        const updatedRowData = {
          ...fileData,
          formsData: JSON.stringify(extractionData.data),
          isProcessed: true,
          confidenceLevel: confidenceLevel,
          totalPages: totalPages,
          totalForms: totalForms,
          status: 2, // Status 2: Extraction complete
        };

        const updateResponse = await fetch(`${BASE_URL}/FileData/ImportFiles`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          },
          body: JSON.stringify(updatedRowData),
        });

        if (updateResponse.ok) {
          // Store extraction report data if available
          // Report data is now stored directly by Python - no need to store from React

          return {
            success: true,
            message: "Single Form extraction completed and saved successfully!",
            type: "single_form",
            confidenceLevel: confidenceLevel,
            totalPages: totalPages,
            totalForms: totalForms,
          };
        } else {
          throw new Error(`Failed to save to database. Status: ${updateResponse.status}`);
        }
      } else {
        throw new Error("Single Form extraction status failed");
      }
    } else {
      // Parse error response body to get specific error messages (e.g., malware detection)
      let errorMessage = "Single Form extraction upload failed";
      try {
        const errorData = await extractionResponse.json();
        if (errorData.error) {
          errorMessage = errorData.details || errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Process pack files (uploadType 11)
   */
  async processPackFile(fileData) {
    const formData = new FormData();
    formData.append("filename", fileData.fileImportId);
    formData.append("original_filename", fileData.fileName);
    formData.append("request_id", fileData.fileImportId);
    formData.append("extension", fileData.fileName.split(".").pop());
    formData.append("organization_id", getUserIdFromLocalStorage().organizationId);
    formData.append("user_id", getUserIdFromLocalStorage().userId);
    formData.append("business_unit", fileData.businessUnit || "Demo");
    formData.append("upload_type", String(fileData.uploadType || 11));

    const url = `${pythonBaseUrl}/Split/AzureSplitExtract`;

    const response = await fetch(url, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      mode: "cors",
    });

    if (response.ok) {
      const splitResponse = await response.json();

      if (splitResponse.status === "success") {
        // For bulk processing, we'll handle pack files differently
        // This is a simplified version - you may need to extend based on your requirements
        return {
          success: true,
          message: "Pack file processed successfully",
          type: "pack",
          data: splitResponse,
        };
      } else {
        throw new Error("Pack splitting failed");
      }
    } else {
      // Parse error response body to get specific error messages (e.g., malware detection)
      let errorMessage = "Pack file upload failed";
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.details || errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Process regular files (uploadType 1)
   */
  async processRegularFile(fileData) {
    const formData = new FormData();
    formData.append("filename", fileData.fileImportId);
    formData.append("original_filename", fileData.fileName);
    formData.append("request_id", fileData.fileImportId);
    formData.append("extension", fileData.fileName.split(".").pop());
    formData.append("organization_id", getUserIdFromLocalStorage().organizationId);
    formData.append("user_id", getUserIdFromLocalStorage().userId);
    formData.append("business_unit", fileData.businessUnit || "Demo");
    formData.append("upload_type", String(fileData.uploadType || 1));

    const url = `${pythonBaseUrl}/Extract/Azure-Upload-File`;

    const response = await fetch(url, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      mode: "cors",
    });

    if (response.ok) {
      // For bulk processing, we might want to implement polling differently
      // This is a simplified version
      return {
        success: true,
        message: "Regular file uploaded successfully",
        type: "regular",
      };
    } else {
      // Parse error response body to get specific error messages (e.g., malware detection)
      let errorMessage = "Regular file upload failed";
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.details || errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // Use default error message
      }
      throw new Error(errorMessage);
    }
  }

  /**
   * Get document type based on upload type
   */
  getDocumentTypeByUploadType(uploadType) {
    switch (uploadType) {
      case 1:
        return "WHS";
      case 2:
        return "W-9";
      case 3:
        return "W-8BEN";
      case 4:
        return "W-8BEN-E";
      case 5:
        return "W-8IMY";
      case 6:
        return "W-8ECI";
      case 7:
        return "W-8EXP";
      case 8:
        return "8233";
      case 9:
        return "SELFCERT-IND";
      case 10:
        return "SELFCERT-ENT";
      case 11:
        return "Pack With Forms";
      default:
        return null;
    }
  }

  /**
   * Store reports (if needed for bulk processing)
   */
  async storeReports(reportData) {
    try {
      const response = await fetch(`${BASE_URL}/FileImportReport/Report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
          "Content-Type": "application/json",
          accept: "*/*",
        },
        body: JSON.stringify(reportData),
      });

      if (!response.ok) {
        throw new Error(`Failed with status: ${response.status}`);
      }

      // Try to parse as JSON, but handle plain text responses
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        // Backend returned plain text instead of JSON
        const textResponse = await response.text();
        data = { message: textResponse };
      }

      console.log("Report stored successfully:", data);
      return { success: true, data };
    } catch (error) {
      console.error("Failed to store report:", error);
      return { success: false, error };
    }
  }
}

export default UploadExtractionService;
